import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useCertificationCategoryApi } from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function CertificationCategoriesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useCertificationCategoryApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('certificationCategory.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'edit', label: t('tenant.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('tenant.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('certification.edit') : t('certificationCategory.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('certification.delete')}
        message={t('approvalFlow.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('certificationCategory.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('EM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('EM') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
